import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, Coins, CreditCard, MapPin, ChevronDown, ChevronUp, Sparkles, Zap, Store, Clock, Tag } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => `R$ ${(v || 0).toFixed(2).replace(".", ",")}`;

export default function Checkout() {
  const { user } = useAuth();
  const { cart, subtotal, setQty, removeItem, clear, addItem } = useCart();
  const nav = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [useCb, setUseCb] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [settings, setSettings] = useState({ checkout_card_fee_pct: 4.99, checkout_pix_fee_pct: 0.99 });
  const [contactName, setContactName] = useState(user?.name || "");
  const [contactPhone, setContactPhone] = useState(user?.phone || "");
  const [addr, setAddr] = useState({ street: "", number: "", complement: "", neighborhood: "", city: "", state: "", zip_code: "" });
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [moreProducts, setMoreProducts] = useState([]);
  const [moreCategory, setMoreCategory] = useState("all");
  const [showMore, setShowMore] = useState(false);
  // Default de fulfillment baseado nas capacidades da loja, com override manual do cliente
  const [fulfillmentChoice, setFulfillmentChoice] = useState(null);
  const fulfillment = (() => {
    if (fulfillmentChoice) return fulfillmentChoice;
    if (!companyInfo) return "pickup";
    if (companyInfo.has_delivery) return "delivery";
    return "pickup";
  })();
  const chooseFulfillment = (m) => setFulfillmentChoice(m);

  useEffect(() => {
    if (cart?.companyId) {
      api.get(`/companies/${cart.companyId}`).then((r) => setCompanyInfo(r.data)).catch(() => {});
      api.get(`/products/company/${cart.companyId}?page_size=50`).then((r) => setMoreProducts(r.data?.items || [])).catch(() => {});
    }
  }, [cart?.companyId]);

  useEffect(() => {
    if (user?.role === "CUSTOMER") api.get("/cashback/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings((prev) => ({...prev, ...r.data}))).catch(() => {});
    // Pre-fill from profile
    api.get("/auth/me").then((r) => {
      const u = r.data;
      if (u.name) setContactName(u.name);
      if (u.phone) setContactPhone(u.phone);
      setAddr((prev) => ({
        street: u.address_street || prev.street,
        number: u.address_number || prev.number,
        complement: u.address_complement || prev.complement,
        neighborhood: u.address_neighborhood || prev.neighborhood,
        city: u.address_city || prev.city,
        state: u.address_state || prev.state,
        zip_code: u.address_zip_code || prev.zip_code,
      }));
      // Abrir bloco de endereço automaticamente se faltam campos obrigatórios
      if (!u.address_street || !u.address_city || !u.address_state) setAddrOpen(true);
    }).catch(() => {});
  }, [user]);

  if (!user) return (
    <div className="max-w-md mx-auto p-8 text-center">
      <h2 className="font-display text-2xl font-bold">Faça login para finalizar a compra</h2>
      <Button onClick={() => nav("/login")} className="mt-4 bg-blue-900 text-white rounded-xl">Entrar</Button>
    </div>
  );
  if (user.role !== "CUSTOMER") return (
    <div className="max-w-md mx-auto p-8 text-center text-stone-600">Apenas clientes podem comprar.</div>
  );
  if (!cart || cart.items.length === 0) return (
    <div className="max-w-md mx-auto p-10 text-center" data-testid="empty-cart">
      <ShoppingCart size={48} className="mx-auto text-stone-300 mb-3"/>
      <h2 className="font-display text-2xl font-bold">Carrinho vazio</h2>
      <p className="text-stone-500 mt-1">Adicione produtos antes de finalizar.</p>
      <Button onClick={() => nav("/")} className="mt-5 bg-blue-900 text-white rounded-xl">Voltar a explorar</Button>
    </div>
  );

  const maxCb = Math.min(wallet?.balance || 0, subtotal);
  const mpAmount = Math.max(0, subtotal - parseFloat(useCb || 0));
  // Modelo HÍBRIDO de taxa:
  //  - PIX: plataforma absorve a taxa (cliente paga R$ 0 extra)
  //  - Cartão: cliente paga a taxa (somada ao subtotal)
  const feePct = paymentMethod === "card" ? (settings.checkout_card_fee_pct || 4.99) : 0;
  const gatewayFee = (paymentMethod === "card" && mpAmount > 0) ? +(mpAmount * feePct / 100).toFixed(2) : 0;
  const mpCharge = +(mpAmount + gatewayFee).toFixed(2);
  const expectedEarn = cart.items.reduce((a, i) => a + (i.unit_price * i.qty * (i.cashback_pct_on_checkout || 0) / 100), 0);
  const expectedTurbo = cart.items.reduce((a, i) => a + (i.unit_price * i.qty * (i.turbo_cashback_pct_on_checkout || 0) / 100), 0);
  const expectedEarnAdjusted = subtotal > 0 ? (expectedEarn * mpAmount / subtotal) : 0;
  const expectedTurboAdjusted = subtotal > 0 ? (expectedTurbo * mpAmount / subtotal) : 0;

  const checkout = async () => {
    if (!contactName || !contactPhone) { toast.error("Nome e telefone de contato obrigatórios"); setAddrOpen(true); return; }
    const needsAddress = fulfillment === "delivery";
    if (needsAddress && (!addr.street || !addr.city || !addr.state)) { toast.error("Endereço incompleto"); setAddrOpen(true); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        company_id: cart.companyId,
        items: cart.items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        use_cashback: parseFloat(useCb) || 0,
        contact_name: contactName, contact_phone: contactPhone,
        address: fulfillment === "delivery" ? addr : null,
        fulfillment_type: fulfillment,
        notes,
        payment_method: paymentMethod,
      });
      if (data.status === "paid") {
        toast.success("Pedido pago integralmente com BemCash!");
        clear();
        nav("/checkout/success?order=" + data.id);
      } else if (data.init_point || data.sandbox_init_point) {
        window.location.href = data.init_point || data.sandbox_init_point;
        clear();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addrSummary = [addr.street, addr.number].filter(Boolean).join(", ") + (addr.city ? ` — ${addr.city}/${addr.state}` : "");
  const addrComplete = !!(addr.street && addr.city && addr.state);

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
      <Link to="/" className="text-xs sm:text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-1 mb-2 sm:mb-3"><ArrowLeft size={14}/> Voltar</Link>
      <h1 className="font-display text-2xl sm:text-4xl font-bold text-stone-900 flex items-center gap-2"><ShoppingCart size={22} className="sm:hidden"/><ShoppingCart size={28} className="hidden sm:inline"/> Checkout</h1>
      <p className="text-stone-500 mt-0.5 text-xs sm:text-sm">Comprando em <strong>{cart.companyName}</strong></p>

      <div className="grid lg:grid-cols-3 gap-3 sm:gap-6 mt-4 sm:mt-6">
        <div className="lg:col-span-2 space-y-3 sm:space-y-4">
          {/* Itens compactos */}
          <Card className="p-3 sm:p-5 rounded-2xl border-stone-200">
            <h3 className="font-display text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center gap-1.5"><ShoppingCart size={16}/> Itens ({cart.items.length})</h3>
            <div className="divide-y divide-stone-100">
              {cart.items.map((it) => (
                <div key={it.product_id} className="py-2 sm:py-3 flex gap-2 sm:gap-3 items-center" data-testid={`cart-item-${it.product_id}`}>
                  {it.image_url && <img src={it.image_url} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover bg-stone-100 shrink-0"/>}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold line-clamp-1">{it.title}</div>
                    <div className="text-[10px] sm:text-xs text-stone-500 flex flex-wrap gap-x-1.5">
                      <span>{fmt(it.unit_price)}</span>
                      {it.cashback_pct_on_checkout > 0 && <span className="text-emerald-700 font-semibold">{it.cashback_pct_on_checkout}% BemCash</span>}
                      {it.turbo_cashback_pct_on_checkout > 0 && <span className="text-amber-700 font-semibold">⚡+{it.turbo_cashback_pct_on_checkout}% Turbo</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg p-0" onClick={() => setQty(it.product_id, it.qty - 1)}><Minus size={11}/></Button>
                    <span className="font-display text-xs sm:text-sm font-bold w-5 sm:w-6 text-center">{it.qty}</span>
                    <Button size="icon" variant="outline" className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg p-0" onClick={() => setQty(it.product_id, it.qty + 1)}><Plus size={11}/></Button>
                  </div>
                  <div className="font-display font-bold w-16 sm:w-20 text-right text-xs sm:text-sm">{fmt(it.unit_price * it.qty)}</div>
                  <button onClick={() => removeItem(it.product_id)} className="text-red-500 ml-0.5 sm:ml-1 p-1"><Trash2 size={13}/></button>
                </div>
              ))}
            </div>
          </Card>

          {/* Adicionar mais itens da MESMA loja (estilo iFood) */}
          {moreProducts.length > 0 && (() => {
            const inCartIds = new Set((cart.items || []).map((i) => i.product_id));
            const available = moreProducts.filter((p) => !inCartIds.has(p.id) && p.price > 0 && p.checkout_enabled);
            if (available.length === 0) return null;
            const cats = ["all", ...Array.from(new Set(available.map((p) => p.category).filter(Boolean)))];
            const filtered = moreCategory === "all" ? available : available.filter((p) => p.category === moreCategory);
            return (
              <Card className="rounded-2xl border-stone-200 overflow-hidden" data-testid="more-products-card">
                <button
                  type="button"
                  onClick={() => setShowMore((v) => !v)}
                  className="w-full p-3 sm:p-4 flex items-center gap-2 sm:gap-3 hover:bg-stone-50 transition-colors text-left border-b border-stone-100"
                  data-testid="more-products-toggle"
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-amber-100 text-amber-700"><Plus size={16}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm sm:text-base text-stone-900">Adicionar mais desta loja</div>
                    <div className="text-[11px] sm:text-xs text-stone-500 line-clamp-1">{available.length} produto(s) disponível(is) em {cart.companyName}</div>
                  </div>
                  {showMore ? <ChevronUp size={16} className="text-stone-400 shrink-0"/> : <ChevronDown size={16} className="text-stone-400 shrink-0"/>}
                </button>
                {showMore && (
                  <div className="p-3 sm:p-4">
                    {cats.length > 2 && (
                      <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-0.5">
                        {cats.map((c) => (
                          <button key={c} onClick={() => setMoreCategory(c)} className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap ${moreCategory === c ? "bg-blue-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`} data-testid={`more-cat-${c}`}>
                            {c !== "all" && <Tag size={10}/>}{c === "all" ? "Tudo" : c}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {filtered.slice(0, 12).map((p) => (
                        <div key={p.id} className="rounded-xl bg-white border border-stone-200 overflow-hidden flex flex-col" data-testid={`more-prod-${p.id}`}>
                          {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full aspect-square object-cover"/> : <div className="w-full aspect-square bg-stone-100"/>}
                          <div className="p-2 flex-1 flex flex-col">
                            <div className="text-[11px] font-semibold text-stone-900 leading-tight line-clamp-2 min-h-[2rem]">{p.title}</div>
                            <div className="flex items-center justify-between mt-1.5 gap-1">
                              <span className="font-bold text-blue-950 text-xs">R$ {p.price.toFixed(2).replace(".", ",")}</span>
                              <button
                                onClick={() => addItem(cart.companyId, cart.companyName, {
                                  product_id: p.id, title: p.title, unit_price: p.price,
                                  image_url: p.image_url, cashback_pct_on_checkout: p.cashback_pct_on_checkout || 0,
                                  turbo_cashback_pct_on_checkout: p.turbo_active ? (p.turbo_cashback_pct || 0) : 0,
                                })}
                                className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-900 hover:bg-blue-950 text-white shrink-0"
                                data-testid={`more-add-${p.id}`}
                              >
                                <Plus size={11}/>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })()}

          {/* DESTAQUE: Cashback ganhar */}
          {(expectedEarnAdjusted > 0 || expectedTurboAdjusted > 0) && (
            <Card className="p-3 sm:p-4 rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-amber-50 shadow-sm" data-testid="cashback-highlight">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1.5 text-emerald-900">
                  <Sparkles size={16} className="text-emerald-700"/>
                  <span className="text-xs sm:text-sm font-bold uppercase tracking-wider">Você vai ganhar</span>
                </div>
                <div className="font-display text-2xl sm:text-3xl font-bold text-emerald-800">
                  + {fmt(expectedEarnAdjusted + expectedTurboAdjusted)}
                </div>
              </div>
              <div className="text-[11px] sm:text-xs text-stone-700 space-y-0.5">
                {expectedEarnAdjusted > 0 && (
                  <div className="flex justify-between"><span className="flex items-center gap-1"><Coins size={11} className="text-emerald-700"/> BemCash padrão</span><span className="font-semibold">+ {fmt(expectedEarnAdjusted)}</span></div>
                )}
                {expectedTurboAdjusted > 0 && (
                  <div className="flex justify-between text-amber-900"><span className="flex items-center gap-1"><Zap size={11} className="text-amber-600 fill-amber-400"/> Turbo Cashback</span><span className="font-bold">+ {fmt(expectedTurboAdjusted)}</span></div>
                )}
              </div>
              <p className="text-[10px] text-stone-500 mt-1.5">Crédito automático após confirmação do pagamento.</p>
            </Card>
          )}

          {/* Entrega/Retirada: cliente escolhe quando ambos disponíveis */}
          {(() => {
            const hasDelivery = !!companyInfo?.has_delivery;
            const hasPickup = companyInfo?.pickup_available !== false;
            const bothAvailable = hasDelivery && hasPickup;
            return (
              <Card className="rounded-2xl border-stone-200 overflow-hidden">
                {/* Toggle Delivery/Retirada quando ambos disponíveis */}
                {bothAvailable && (
                  <div className="p-3 sm:p-4 border-b border-stone-100 bg-stone-50/50">
                    <Label className="text-xs font-bold text-stone-700 mb-2 block">Como você quer receber?</Label>
                    <div className="grid grid-cols-2 gap-2" data-testid="fulfillment-toggle">
                      <button
                        type="button"
                        onClick={() => chooseFulfillment("delivery")}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all text-left ${fulfillment === "delivery" ? "bg-emerald-50 border-emerald-500 ring-2 ring-emerald-200" : "bg-white border-stone-200 hover:border-stone-300"}`}
                        data-testid="fulfillment-delivery"
                      >
                        <MapPin size={16} className={fulfillment === "delivery" ? "text-emerald-700" : "text-stone-400"}/>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-bold ${fulfillment === "delivery" ? "text-emerald-900" : "text-stone-700"}`}>Receber em casa</div>
                          <div className="text-[10px] text-stone-500">Entrega no seu endereço</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => chooseFulfillment("pickup")}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all text-left ${fulfillment === "pickup" ? "bg-blue-50 border-blue-600 ring-2 ring-blue-200" : "bg-white border-stone-200 hover:border-stone-300"}`}
                        data-testid="fulfillment-pickup"
                      >
                        <Store size={16} className={fulfillment === "pickup" ? "text-blue-800" : "text-stone-400"}/>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-bold ${fulfillment === "pickup" ? "text-blue-900" : "text-stone-700"}`}>Retirar na loja</div>
                          <div className="text-[10px] text-stone-500">Sem custo de entrega</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Conteúdo de acordo com o método escolhido */}
                {fulfillment === "delivery" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setAddrOpen((v) => !v)}
                      className="w-full p-3 sm:p-4 flex items-center gap-2 sm:gap-3 hover:bg-stone-50 transition-colors text-left"
                      data-testid="addr-toggle"
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${addrComplete ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        <MapPin size={16}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-sm sm:text-base text-stone-900">Endereço de entrega</div>
                        <div className="text-[11px] sm:text-xs text-stone-500 line-clamp-1">
                          {addrComplete ? addrSummary : <span className="text-amber-700 font-semibold">Preencha o endereço para entrega</span>}
                        </div>
                      </div>
                      {addrOpen ? <ChevronUp size={16} className="text-stone-400 shrink-0"/> : <ChevronDown size={16} className="text-stone-400 shrink-0"/>}
                    </button>
                    {addrOpen && (
                      <div className="p-3 sm:p-4 border-t border-stone-200">
                        <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-3">
                          <div className="col-span-2 sm:col-span-2"><Label className="text-xs">Nome de contato</Label><Input required value={contactName} onChange={(e) => setContactName(e.target.value)} className="rounded-xl mt-1 h-9 text-sm" data-testid="ck-name"/></div>
                          <div><Label className="text-xs">Telefone</Label><Input required value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="(79) 99999-9999" className="rounded-xl mt-1 h-9 text-sm" data-testid="ck-phone"/></div>
                          <div><Label className="text-xs">CEP</Label><Input value={addr.zip_code} onChange={(e) => setAddr({...addr, zip_code: e.target.value})} className="rounded-xl mt-1 h-9 text-sm"/></div>
                          <div className="col-span-2"><Label className="text-xs">Rua</Label><Input required value={addr.street} onChange={(e) => setAddr({...addr, street: e.target.value})} className="rounded-xl mt-1 h-9 text-sm" data-testid="ck-street"/></div>
                          <div><Label className="text-xs">Número</Label><Input value={addr.number} onChange={(e) => setAddr({...addr, number: e.target.value})} className="rounded-xl mt-1 h-9 text-sm"/></div>
                          <div><Label className="text-xs">Complemento</Label><Input value={addr.complement} onChange={(e) => setAddr({...addr, complement: e.target.value})} className="rounded-xl mt-1 h-9 text-sm"/></div>
                          <div><Label className="text-xs">Bairro</Label><Input value={addr.neighborhood} onChange={(e) => setAddr({...addr, neighborhood: e.target.value})} className="rounded-xl mt-1 h-9 text-sm"/></div>
                          <div><Label className="text-xs">Cidade</Label><Input required value={addr.city} onChange={(e) => setAddr({...addr, city: e.target.value})} className="rounded-xl mt-1 h-9 text-sm" data-testid="ck-city"/></div>
                          <div><Label className="text-xs">UF</Label><Input required value={addr.state} onChange={(e) => setAddr({...addr, state: e.target.value})} maxLength={2} className="rounded-xl mt-1 h-9 text-sm"/></div>
                          <div className="col-span-2"><Label className="text-xs">Observações (opcional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-xl mt-1 text-sm"/></div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-3 sm:p-4 bg-blue-50/40" data-testid="pickup-card">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-100 text-blue-900 shrink-0"><Store size={16}/></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-sm sm:text-base text-stone-900">Retirada em loja</div>
                        <div className="text-[11px] sm:text-xs text-stone-600 mt-0.5">{companyInfo?.address || cart.companyName}</div>
                        {companyInfo?.business_hours && (
                          <div className="text-[11px] sm:text-xs text-stone-700 mt-1 flex items-start gap-1">
                            <Clock size={11} className="mt-0.5 text-stone-400 shrink-0"/>
                            <span className="whitespace-pre-line">{companyInfo.business_hours}</span>
                          </div>
                        )}
                        {companyInfo?.lat && companyInfo?.lng && (
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${companyInfo.lat},${companyInfo.lng}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg bg-blue-900 hover:bg-blue-950 text-white text-[11px] sm:text-xs font-semibold"
                            data-testid="pickup-directions"
                          >
                            <MapPin size={12}/> Como chegar
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-blue-200 grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="col-span-2"><Label className="text-xs">Nome de contato</Label><Input required value={contactName} onChange={(e) => setContactName(e.target.value)} className="rounded-xl mt-1 h-9 text-sm bg-white" data-testid="ck-name"/></div>
                      <div className="col-span-2"><Label className="text-xs">Telefone</Label><Input required value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="(79) 99999-9999" className="rounded-xl mt-1 h-9 text-sm bg-white" data-testid="ck-phone"/></div>
                      <div className="col-span-2"><Label className="text-xs">Observações (opcional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-xl mt-1 text-sm bg-white"/></div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })()}
        </div>

        <div className="space-y-3 sm:space-y-4">
          <Card className="p-3 sm:p-5 rounded-2xl border-stone-200 lg:sticky lg:top-20">
            <h3 className="font-display text-base sm:text-lg font-semibold mb-2 sm:mb-3">Resumo</h3>
            <div className="flex justify-between text-xs sm:text-sm py-1"><span>Subtotal</span><span className="font-bold">{fmt(subtotal)}</span></div>

            {wallet && wallet.balance > 0 && (
              <div className="mt-2 sm:mt-3 p-2.5 sm:p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="flex items-center gap-1.5 mb-1.5"><Coins size={13} className="text-emerald-700"/><span className="text-[11px] sm:text-xs font-semibold text-emerald-900">Seu saldo: {fmt(wallet.balance)}</span></div>
                <Label className="text-[11px] text-emerald-900">Usar (até {fmt(maxCb)})</Label>
                <Input type="number" step="0.01" min="0" max={maxCb} value={useCb} onChange={(e) => setUseCb(e.target.value)} className="rounded-xl mt-1 bg-white h-9 text-sm" data-testid="ck-use-cashback"/>
                <div className="flex gap-1 mt-1.5">
                  <Button size="sm" variant="outline" onClick={() => setUseCb(0)} className="rounded-lg text-[11px] flex-1 h-7">Nada</Button>
                  <Button size="sm" variant="outline" onClick={() => setUseCb((maxCb / 2).toFixed(2))} className="rounded-lg text-[11px] flex-1 h-7">50%</Button>
                  <Button size="sm" variant="outline" onClick={() => setUseCb(maxCb.toFixed(2))} className="rounded-lg text-[11px] flex-1 h-7">Máx</Button>
                </div>
              </div>
            )}

            <div className="border-t border-stone-200 mt-3 pt-2 sm:pt-3 space-y-1 sm:space-y-1.5 text-xs sm:text-sm">
              {parseFloat(useCb || 0) > 0 && <div className="flex justify-between"><span>Pago em BemCash</span><span className="text-emerald-700 font-bold">- {fmt(useCb)}</span></div>}
              {mpAmount > 0 && (
                <>
                  <div className="mt-2 sm:mt-3 p-2.5 sm:p-3 rounded-xl border border-blue-100 bg-blue-50/40">
                    <Label className="text-[11px] font-bold text-blue-950">Forma de pagamento</Label>
                    <div className="flex gap-1.5 mt-1.5">
                      <button onClick={() => setPaymentMethod("pix")} className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold border ${paymentMethod==="pix"?"bg-emerald-700 text-white border-emerald-700":"bg-white text-stone-700 border-stone-200"}`} data-testid="pm-pix">
                        PIX <span className="text-[9px] opacity-90">sem taxa ✓</span>
                      </button>
                      <button onClick={() => setPaymentMethod("card")} className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold border ${paymentMethod==="card"?"bg-blue-900 text-white border-blue-900":"bg-white text-stone-700 border-stone-200"}`} data-testid="pm-card">
                        Cartão <span className="text-[9px] opacity-90">+{(settings.checkout_card_fee_pct||4.99).toFixed(2)}%</span>
                      </button>
                    </div>
                    <p className="text-[10px] text-stone-500 mt-1.5">
                      {paymentMethod === "pix"
                        ? "✓ Plataforma absorve a taxa PIX."
                        : "Taxa do cartão repassada ao cliente. PIX é mais barato."}
                    </p>
                  </div>
                  <div className="flex justify-between"><span>Subtotal {paymentMethod === "card" ? "(MP)" : "(PIX)"}</span><span>{fmt(mpAmount)}</span></div>
                  {paymentMethod === "card" && gatewayFee > 0 && (
                    <div className="flex justify-between text-amber-700"><span>Taxa cartão ({feePct.toFixed(2)}%)</span><span>+ {fmt(gatewayFee)}</span></div>
                  )}
                  <div className="flex justify-between font-semibold"><span>A pagar no MP</span><span>{fmt(mpCharge)}</span></div>
                </>
              )}
              <div className="flex justify-between text-sm sm:text-base pt-2 border-t border-stone-200"><span className="font-display font-semibold">Total a pagar</span><span className="font-display font-bold text-blue-950">{fmt(parseFloat(useCb||0) + mpCharge)}</span></div>
            </div>

            <Button onClick={checkout} disabled={busy} className="mt-3 sm:mt-4 w-full bg-blue-900 hover:bg-blue-950 text-white rounded-xl h-11 sm:h-12 font-bold text-sm" data-testid="ck-pay">
              <CreditCard size={15} className="mr-1.5"/>
              {busy ? "Processando..." : mpAmount === 0 ? "Pagar em BemCash" : `Pagar ${fmt(parseFloat(useCb||0) + mpCharge)}`}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
